/**
 * Pure projection of a server-side code-execution tool result.
 *
 * The provider returns a discriminated union (`code_execution_result`,
 * `bash_code_execution_result`, the `text_editor_*` variants, plus the
 * `*_tool_result_error` members). We read the fields common to the success
 * members and treat `error_code` as the error path — anything that isn't from
 * that family falls through to the generic JSON view.
 */

import { asString, isRecord } from './entity-view';

/** Max stdout/stderr characters rendered inline before truncation. */
export const MAX_STREAM_CHARS = 1200;

export interface CodeExecutionView {
  stdout: string | null;
  stderr: string | null;
  returnCode: number | null;
  fileCount: number;
  errorCode: string | null;
}

export function buildCodeExecutionView(output: unknown): CodeExecutionView | null {
  if (!isRecord(output)) return null;
  const type = asString(output.type);
  // Only claim outputs that actually came from the code-execution tool family.
  // Anything else falls through to the generic JSON view.
  if (!type || !type.includes('code_execution')) return null;

  const errorCode = asString(output.error_code);
  const stdout = typeof output.stdout === 'string' ? output.stdout : null;
  const stderr = typeof output.stderr === 'string' ? output.stderr : null;
  const returnCode = typeof output.return_code === 'number' ? output.return_code : null;
  const fileCount = Array.isArray(output.content) ? output.content.length : 0;

  // An entry with neither streams, nor an exit code, nor an error tells the
  // reader nothing — let the JSON fallback show whatever it actually is.
  if (stdout === null && stderr === null && returnCode === null && errorCode === null) {
    return null;
  }
  return { stdout, stderr, returnCode, fileCount, errorCode };
}

export function truncateStream(value: string): { text: string; truncated: boolean } {
  if (value.length <= MAX_STREAM_CHARS) return { text: value, truncated: false };
  return { text: value.slice(0, MAX_STREAM_CHARS), truncated: true };
}
